import alert_mixin from '@/utils/mixins/alert_mixin';
import axios from 'axios';
import { fetch_form_structure } from '@/services/main_section_service';
import {
  CREATE_ONBOARDING_VERIFICATION_QUESTION_MAPPINGS_API,
  GET_ONBOARDING_VERIFICATION_QUESTION_MAPPINGS_API,
  get_request_config,
} from '@/utils/system_constants/constant.js';

/* eslint-disable-next-line no-unused-vars */
const VERIFICATION_ID_KEYS = [
  'verification_section_question_id',
  'verification_section_question',
  'verification_form_section_question_id',
  'verification_form_section_question',
  'verification_question_id',
  'verification_question',
  'verification_id',
];

/* eslint-disable-next-line no-unused-vars */
const QUESTION_LABEL_KEYS = [
  'question_text',
  'question',
  'text',
  'name',
];

/* eslint-disable-next-line no-unused-vars */
const ONBOARDING_ID_KEYS = [
  'onboarding_section_question_id',
  'onboarding_section_question',
  'onboarding_form_section_question_id',
  'onboarding_form_section_question',
  'onboarding_question_id',
  'onboarding_question',
  'onboarding_id',
];


export default {
  name: 'FormQuestionMapping',
  mixins: [alert_mixin],
  emits: ['go_to_verification_form', 'go_to_onboarding_form'],
  props: {
    embedded: {
      type: Boolean,
      default: false,
    },
    onboarding_form_id: {
      type: [String, Number],
      default: '',
    },
    onboarding_form_name: {
      type: String,
      default: '',
    },
    verification_survey_form_id: {
      type: [String, Number],
      default: '',
    },
    verification_form_name: {
      type: String,
      default: '',
    },
    form_link_id: {
      type: [String, Number],
      default: '',
    },
  },
  data: () => ({
    loading: false,
    saving: false,
    onboarding_questions: [],
    verification_questions: [],
    mappings: {},
    loading_existing_mappings: false,
    reload_request_id: 0,
  }),
  watch: {
    onboarding_form_id: {
      handler() {
        this.reload_all();
      },
      immediate: true,
    },
    verification_survey_form_id: {
      handler() {
        this.reload_all();
      },
      immediate: true,
    },
    form_link_id: {
      handler() {
        this.reload_all();
      },
      immediate: true,
    },
  },
  computed: {
    verification_to_onboarding() {
      const next = {};
      const mappings = this.mappings || {};
      Object.keys(mappings).forEach((onboarding_id) => {
        const verification_id = Reflect.get(mappings, onboarding_id);
        if (!verification_id) return;
        next[String(verification_id)] = String(onboarding_id);
      });
      return next;
    },
    empty_state() {
      if (this.loading) return null;

      const has_verification_questions = (this.verification_questions || []).length > 0;
      const has_onboarding_questions = (this.onboarding_questions || []).length > 0;

      if (!has_verification_questions) {
        return this.build_empty_state(
          'verification',
          this.verification_form_name,
          'go_to_verification_form',
        );
      }

      if (!has_onboarding_questions) {
        return this.build_empty_state(
          'onboarding',
          this.onboarding_form_name,
          'go_to_onboarding_form',
        );
      }

      return null;
    },
    mapped_count() {
      const verification_ids = (this.verification_questions || []).map((question_item) => String(question_item.id));
      return verification_ids.filter((verification_id) => Boolean(Reflect.get(this.verification_to_onboarding, verification_id))).length;
    },
  },
  methods: {
    build_empty_state(type, form_name, action_event) {
      const trimmed_name = String(form_name || '').trim();
      const safe_type = String(type || '').trim() || 'form';
      const empty_state = {
        title: `No ${safe_type} questions found`,
        description: trimmed_name
          ? `The ${trimmed_name} ${safe_type} form has no questions yet. Add questions to that form before mapping.`
          : `This ${safe_type} form has no questions yet. Add questions to that form before mapping.`,
        action_label: `Go to ${safe_type} form`,
        action_event,
      };

      return empty_state;
    },

    try_parse_json(value) {
      if (typeof value !== 'string') return null;
      const trimmed = value.trim();

      if (
        !(
          (trimmed.startsWith('{') && trimmed.endsWith('}')) ||
          (trimmed.startsWith('[') && trimmed.endsWith(']'))
        )
      ) {
        return null;
      }

      return JSON.parse(trimmed);
    },

    parse_json_safely(data) {
      if (typeof data !== 'string') return data;
      const parsed = this.try_parse_json(data);
      return parsed || {};
    },

    parse_response_data(response) {
      let data = response && response.data != null ? response.data : response != null ? response : {};
      data = this.parse_json_safely(data);
      if (typeof data === 'string') {
        data = this.parse_json_safely(data);
      }
      return typeof data === 'string' ? {} : data;
    },

    get_selected_onboarding_question(verification_question_id) {
      const selected_onboarding_id = this.verification_to_onboarding?.[String(verification_question_id)];
      if (!selected_onboarding_id) return null;
      return (
        (this.onboarding_questions || []).find(
          (question_item) => String(question_item.id) === String(selected_onboarding_id),
        ) || null
      );
    },

    on_change_onboarding_selection(verification_question_id, onboarding_question_id_raw) {
      const verification_question_id_string = String(verification_question_id || '').trim();
      const onboarding_question_id_string = String(onboarding_question_id_raw || '').trim();

      if (!verification_question_id_string) return;

      const next = { ...(this.mappings || {}) };

      // Remove any existing onboarding mapping that points to this verification question.
      Object.keys(next).forEach((onboarding_id) => {
        if (
          String(Reflect.get(next, onboarding_id)) ===
          verification_question_id_string
        ) {
          Reflect.deleteProperty(next, onboarding_id);
        }
      });
      // If user cleared selection, we're done.
      if (!onboarding_question_id_string) {
        this.mappings = next;
        return;
      }

      // Enforce one onboarding question -> one verification question.
      Reflect.set(next, onboarding_question_id_string, verification_question_id_string);
      this.mappings = next;
    },

    get_question_id(question, fallback_id) {
      const candidates = [
        question?.section_question_id,
        question?.form_section_question_id,
        question?.section_question,
        question?.id,
        question?.question_id,
      ];

      const found = candidates
        .map((value) => this.normalize_question_id_candidate(value))
        .find((value) => value != null && String(value).trim() !== '');

      return found != null ? found : fallback_id;
    },

    normalize_question_id_candidate(value) {
      if (value == null) return null;
      if (typeof value === 'object') {
        if (value.id != null) return value.id;
        if (value.section_question_id != null) return value.section_question_id;
        if (value.question_id != null) return value.question_id;
        return null;
      }
      return value;
    },

    get_label_from_question(question) {
      for (const key of QUESTION_LABEL_KEYS) {
        const value = Reflect.get(question || {}, key);

        if (value != null) {
          return value;
        }
      }

      return '';
    },

    get_question_label(question) {
      const label = this.get_label_from_question(question);
      return String(label).trim();
    },

    get_onboarding_id_from_row(row) {
      for (const key of ONBOARDING_ID_KEYS) {
        const value = Reflect.get(row || {}, key);

        if (value != null) {
          return value;
        }
      }

      return null;
    },

    get_verification_id_from_row(row) {
      for (const key of VERIFICATION_ID_KEYS) {
        const value = Reflect.get(row || {}, key);

        if (value != null) {
          return value;
        }
      }

      return null;
    },

    add_flat_question(result, question, fallback_id) {
      const label = this.get_question_label(question);
      if (!label) return;

      result.push({
        id: this.get_question_id(question, fallback_id),
        label,
      });
    },

    flatten_form_structure(items) {
      const result = [];
      const main_sections = Array.isArray(items) ? items : [];

      main_sections.forEach((main_section, main_index) => {
        const sub_sections = Array.isArray(main_section?.sub_sections) ? main_section.sub_sections : [];

        sub_sections.forEach((sub_section, sub_index) => {
          const questions = Array.isArray(sub_section?.questions) ? sub_section.questions : [];

          questions.forEach((question, questionIndex) => {
            const fallback_id = `${main_index}-${sub_index}-${questionIndex}`;
            this.add_flat_question(result, question, fallback_id);
          });
        });
      });

      return result;
    },

    async reload_all() {
      this.reload_request_id += 1;
      const request_id = this.reload_request_id;

      const onboarding_form_id = Number(this.onboarding_form_id) || null;
      const verification_survey_form_id = Number(this.verification_survey_form_id) || null;
      const form_link_id = String(this.form_link_id || '').trim() || null;

      if (!onboarding_form_id || !verification_survey_form_id) {
        this.onboarding_questions = [];
        this.verification_questions = [];
        this.mappings = {};
        return;
      }

      this.loading = true;
      try {
        const [onboarding_items, verification_items] = await Promise.all([
          fetch_form_structure(onboarding_form_id),
          fetch_form_structure(verification_survey_form_id),
        ]);

        if (request_id !== this.reload_request_id) return;

        this.onboarding_questions = this.flatten_form_structure(onboarding_items);
        this.verification_questions = this.flatten_form_structure(verification_items);

        if (form_link_id) await this.load_existing_mappings();
        else this.mappings = {};
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Failed to load form questions.';
        this.onboarding_questions = [];
        this.verification_questions = [];
        this.show_error(message);
      } finally {
        this.loading = false;
      }
    },

    async load_existing_mappings() {
      const form_link_id = String(this.form_link_id || '').trim() || null;

      if (!form_link_id) {
        this.mappings = {};
        return;
      }

      this.loading_existing_mappings = true;
      try {
        const response = await axios.get(
          GET_ONBOARDING_VERIFICATION_QUESTION_MAPPINGS_API,
          get_request_config({
            params: { form_link_id },
          }),
        );
        const parsed = this.parse_response_data(response);

        if (parsed?.status && parsed.status !== 'success') {
          this.show_error(parsed?.message || 'Failed to load existing mappings.');
          return;
        }

        const items =
          (parsed && (parsed.data != null ? parsed.data : parsed.results)) || [];
        const rows = Array.isArray(items) ? items : [];

        const sorted_rows = [...rows].sort((first_row, second_row) => {
          // @codacy-disable-next-line
          const first_row_id = first_row?.id ?? 0;
          // @codacy-disable-next-line
          const second_row_id = second_row?.id ?? 0;

          return second_row_id - first_row_id;
        });
        const seen = new Set();
        const next = {};

        sorted_rows.forEach((row) => {
          if (row?.is_active === false) return;

          const onboarding_id = this.get_onboarding_id_from_row(row);
          const verification_id = this.get_verification_id_from_row(row);
          if (!onboarding_id || !verification_id) return;

          const key = String(onboarding_id);
          if (seen.has(key)) return;

          seen.add(key);
          Reflect.set(next, key, String(verification_id));
        });

        this.mappings = next;
      } catch (error) {
        const message = error?.response?.data?.message || error?.message || 'Failed to load existing mappings.';
        this.show_error(message);
      } finally {
        this.loading_existing_mappings = false;
      }
    },

    async save_mappings() {
      const form_link_id = String(this.form_link_id || '').trim() || null;
      if (!form_link_id) {
        this.show_error('Missing form link id. Please assign the onboarding form first.');
        return;
      }

      const mappings = Object.entries(this.mappings || {})
        .filter(([, value]) => Boolean(value))
        .map(([onboarding_id, verification_id]) => ({
          onboarding_section_question_id: Number(onboarding_id),
          verification_section_question_id: Number(verification_id),
          is_active: true,
        }))
        .filter((item) => item.onboarding_section_question_id && item.verification_section_question_id);

      if (!mappings.length) {
        this.show_error('Please map at least one question before saving.');
        return;
      }

      const verification_ids = new Set((this.verification_questions || []).map((q) => String(q.id)));
      const invalid = mappings.find(
        (item) => !verification_ids.has(String(item.verification_section_question_id)),
      );
      if (invalid) {
        this.show_error('One or more selected verification questions are not in this verification form.');
        return;
      }

      this.saving = true;
      const loader = await this.show_loader('Saving mappings...');
      try {
        const payload = { form_link_id, mappings };
        const response = await axios.post(
          CREATE_ONBOARDING_VERIFICATION_QUESTION_MAPPINGS_API,
          payload,
          get_request_config(),
        );
        const data = this.parse_response_data(response);

        if (data?.status === 'success') {
          this.show_success(data?.message || 'Mappings saved.');
          await this.load_existing_mappings();
          return;
        }

        this.show_error(data?.message || 'Failed to save mappings.');
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Failed to save mappings.';
        this.show_error(message);
      } finally {
        await this.close_loader(loader);
        this.saving = false;
      }
    },
  },
};
